import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { Menu, PanelLeft, PanelTop, X } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import type { DevelopmentTool, DevelopmentToolsVisibility } from "@/development/preferences";
import { AccountPane } from "@/features/auth/components/AccountBody";
import { AccountSheet } from "@/features/auth/components/AccountSheet";
import { ColumnAccountToggle } from "@/features/auth/components/AccountToggles";
import { clientConfigQuery, useSession } from "@/features/auth/queries";
import { CreditsPane } from "@/features/credits/components/CreditsPane";
import { CreditsPill } from "@/features/credits/components/CreditsPill";
import { CreditsSheet } from "@/features/credits/components/CreditsSheet";
import { chooseArrangement, useArrangement } from "./arrangement";
import { PageFooter } from "./PageFooter";
import type { ShellPanel } from "./panel";
import { ShellHead } from "./ShellHead";
import { useHoverIntent } from "./use-hover-intent";
import { usePhoneWidth } from "./use-phone-width";
import { Ways } from "./Ways";

/**
 * The app's frame, in three arrangements of one idea.
 *
 * On a desktop the ways are either down the side or behind a mark in a bar
 * across the top, and the mark changes which. Changing costs the screen
 * nothing: a desktop window is mostly margin, and the margin is what pays for
 * the chrome. On a phone there is no margin to spend, so the ways become a
 * list that opens downward and your account or credits come up from the foot.
 *
 * Only three things are state here — which arrangement, whether the pointer
 * is holding the column open, and which panel is showing. Every
 * timing and every distance is a CSS rule on the state the frame is in, in
 * `shell.css`.
 */
export function AppShell({
  children,
  developmentTools,
  onDevelopmentToolVisibilityChange,
}: {
  children: ReactNode;
  developmentTools: DevelopmentToolsVisibility;
  onDevelopmentToolVisibilityChange: (tool: DevelopmentTool, visible: boolean) => void;
}) {
  const phone = usePhoneWidth();
  const arrangement = useArrangement();
  const { data: session } = useSession();
  const [peeked, setPeeked] = useState(false);
  const [waysOpen, setWaysOpen] = useState(false);
  const [panel, setPanel] = useState<ShellPanel | null>(null);
  /** What the panel face holds, kept through its leaving so it does not change on the way out. */
  const [panelShown, setPanelShown] = useState<ShellPanel>("account");
  if (panel && panel !== panelShown) setPanelShown(panel);

  // Both panels belong to an account, so signing out puts either away
  // wherever it is standing instead of leaving an empty face behind.
  if (panel && !session?.user) setPanel(null);

  // A link in a panel goes somewhere, and arriving there is the panel's job done.
  const pathname = useRouterState({ select: state => state.location.pathname });
  const { data: clientConfig } = useQuery(clientConfigQuery);
  const immersive =
    pathname.startsWith("/play/") ||
    pathname === "/map-lab" ||
    pathname === "/viewport-lab" ||
    pathname.endsWith("/still");
  // Editors are a workspace, not a page to read to the end of.
  const editing = /^\/(assets\/[^/]+\/edit|presets\/[^/]+\/(edit|new))\/?$/.test(pathname);
  const showPageFooter = !immersive && !editing;
  const [panelPathname, setPanelPathname] = useState(pathname);
  if (pathname !== panelPathname) {
    setPanelPathname(pathname);
    if (panel) setPanel(null);
  }

  /** The two places a pointer can be and still be holding the column open. */
  const markElement = useRef<HTMLButtonElement>(null);
  const columnElement = useRef<HTMLDivElement>(null);

  const { cancel, handlers } = useHoverIntent(
    () => setPeeked(true),
    () => setPeeked(false),
    () =>
      Boolean(markElement.current?.matches(":hover") || columnElement.current?.matches(":hover")),
  );

  const inSidebar = !phone && arrangement === "sidebar";
  /** The column is standing in the frame, whether or not you have kept it. */
  const columnShowing = inSidebar || (!phone && peeked);
  const panelInColumn = inSidebar && panel !== null;
  const panelBeside = !phone && !inSidebar ? panel : null;

  function closePanel() {
    cancel();
    setPanel(null);
  }

  function togglePanel(next: ShellPanel) {
    if (panel === next) closePanel();
    else setPanel(next);
  }

  /** The mark is the way between the two arrangements, both ways round. */
  function changeOver() {
    // Going to the bar has to let go of whatever the pointer was doing.
    // Going to the sidebar keeps it, and the column already standing is what
    // tells the bar it has nothing to wait for.
    if (arrangement === "sidebar") {
      cancel();
      setPeeked(false);
    }
    setPanel(null);
    chooseArrangement(arrangement === "sidebar" ? "bar" : "sidebar");
  }

  const panelOpen = panel !== null;
  useEffect(() => {
    if (!panelOpen) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPanel(null);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [panelOpen]);

  const PanelPane = panelShown === "credits" ? CreditsPane : AccountPane;

  return (
    <div className="h-dvh overflow-hidden bg-background">
      <div
        className="app-shell"
        data-arrangement={arrangement}
        data-column={columnShowing || undefined}
        data-peeked={peeked || undefined}
        data-panel={panel ?? undefined}
        data-ways={(phone && waysOpen) || undefined}
        data-receded={(phone && panelOpen) || undefined}
      >
        <ShellHead
          mark={
            phone
              ? {
                  // Down here the ways are not an arrangement to be in, they
                  // are a list that opens and shuts, and nothing says that
                  // plainer.
                  icon: waysOpen ? X : Menu,
                  label: waysOpen ? "Close navigation" : "Navigation",
                  expanded: waysOpen,
                  onPress: () => setWaysOpen(open => !open),
                }
              : {
                  // Each arrangement shows the one you would get, so the mark
                  // never sits there looking pressed for something already
                  // open.
                  icon: inSidebar ? PanelTop : PanelLeft,
                  label: inSidebar ? "Switch to the top bar" : "Switch to the sidebar",
                  onPress: changeOver,
                }
          }
          peek={phone ? undefined : handlers}
          markRef={markElement}
          panelBeside={panelBeside}
          panelInColumn={panelInColumn}
          pictureInColumn={inSidebar}
          onTogglePanel={togglePanel}
          onLeavePanel={closePanel}
          developmentTools={developmentTools}
          onDevelopmentToolVisibilityChange={onDevelopmentToolVisibilityChange}
        />

        {/* The ways are ground here too. The screen has been lying on them. */}
        <div className="shell-ways-drawer" inert={!phone || !waysOpen}>
          <div>
            <Ways onNavigate={() => setWaysOpen(false)} />
          </div>
        </div>

        {/* It stands in the room the margins gave up, hovered or kept, which
            is what makes pressing the mark not the column arriving. */}
        <div ref={columnElement} className="shell-column" inert={!columnShowing} {...handlers}>
          <div className="shell-panes">
            <div inert={panelInColumn} className="flex h-full w-(--column-width) shrink-0 flex-col">
              <Ways />
              <div
                inert={!inSidebar}
                className="shell-column-foot flex shrink-0 flex-col gap-2 p-2"
              >
                <CreditsPill
                  row
                  className="self-center"
                  open={panelInColumn && panel === "credits"}
                  onToggle={() => togglePanel("credits")}
                />
                <ColumnAccountToggle
                  open={panelInColumn && panel === "account"}
                  onToggle={() => togglePanel("account")}
                />
              </div>
            </div>

            <div inert={!panelInColumn} className="flex h-full w-(--panel-width) shrink-0 flex-col">
              <PanelPane />
            </div>
          </div>
        </div>

        {/* In the top bar a panel is a column of its own on the other side.
            It is the one thing the margin cannot always pay for. */}
        <div className="shell-panel-beside" inert={!panelBeside}>
          <PanelPane />
        </div>

        {/*
          The screen is the window minus whatever is open beside it. Its edges
          move and its content does not, so nothing re-columns.

          Sideways first, then the bar. In the sidebar the screen takes the
          full height and the bar is underneath it rather than faded out;
          letting go spreads the screen back across the window before dropping
          it to uncover the bar again.
        */}
        <div className="app-screen-frame">
          {/* The footer (privacy, terms, and the notice reCAPTCHA asks for) is the page's last
              line, under the content, or at the bottom of the screen when the content is shorter. */}
          <div
            data-scroll-restoration-id="screen"
            className={showPageFooter ? "app-screen flex flex-col *:shrink-0" : "app-screen"}
          >
            {children}
            {showPageFooter && (
              <PageFooter recaptcha={Boolean(clientConfig?.recaptchaWebSiteKey)} />
            )}
          </div>

          {/* Beside a panel, pressing the screen puts it away — the same
              thing pressing the page beside a drawer has always done. */}
          {panelBeside && (
            <button
              type="button"
              aria-label={panelBeside === "credits" ? "Close credits" : "Close account"}
              onClick={closePanel}
              className="absolute inset-0 z-10 cursor-default"
            />
          )}
        </div>
      </div>

      {/* A narrow screen has no side for these to come in from, so they come
          up, over an app that has stepped back to let them. */}
      {phone && (
        <>
          <AccountSheet
            open={panel === "account"}
            onOpenChange={open => setPanel(open ? "account" : null)}
          />
          <CreditsSheet
            open={panel === "credits"}
            onOpenChange={open => setPanel(open ? "credits" : null)}
          />
        </>
      )}
    </div>
  );
}
